import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, GitMerge, Plus, Check, X, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { masterApi, docReqApi, userApi, rbacApi, contractorApi, associateDesignationApi, orgchartApi } from '../lib/api';
import { departmentTree } from '../lib/utils';
import { signatureApi, resolveMediaUrl } from '../lib/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import { useUnit } from '../contexts/UnitContext';
import { useAuth } from '../contexts/AuthContext';

// Module-level so it is NOT recreated on each render (that was causing inputs to lose focus).
function MasterSection({ title, items, kind, newValue, setNewValue, edit, setEdit, wrap, openMerge, wide = false }) {
  const navigate = useNavigate();
  const { setActiveUnit } = useUnit();
  const { hasPerm } = useAuth();
  // Units and departments stay on `master.manage`, which is what reaching this
  // page already requires. Only DESIGNATIONS need the extra, admin-held key —
  // read from the permission system, never from a role name or a label.
  const canAddHere = kind !== 'designation' || hasPerm('master.designation_create');
  const goToFiltered = (it) => {
    if (kind === 'department') navigate(`/employees?department=${encodeURIComponent(it.name)}`);
    else if (kind === 'unit') { setActiveUnit(it.name); navigate('/employees'); }
    else if (kind === 'designation') navigate(`/employees?designation=${encodeURIComponent(it.name)}`);
  };
  const add = (name) => kind === 'department' ? masterApi.addDepartment(name) : kind === 'unit' ? masterApi.addUnit(name) : masterApi.addDesignation(name);
  const rename = (id, name, level) => kind === 'department' ? masterApi.renameDepartment(id, name) : kind === 'unit' ? masterApi.renameUnit(id, name) : masterApi.updateDesignation(id, name, level);
  const del = (id) => kind === 'department' ? masterApi.deleteDepartment(id) : kind === 'unit' ? masterApi.deleteUnit(id) : masterApi.deleteDesignation(id);
  // Departments nest two deep — a department, a sub-unit inside it, a sub-unit
  // inside that (Sales > SCM > FG Store) — so anything that is not already two
  // levels down may be offered to put something inside.
  const groupChoices = kind !== 'department' ? []
    : items.filter((x) => (x.depth || 0) < 2).map((x) => x.name);
  // Editing a department saves the rename and the group together, so one Save
  // never leaves half the change applied.
  const saveRow = async (it) => {
    const level = edit.level === '' || edit.level == null ? null : parseInt(edit.level, 10);
    await wrap(async () => {
      // A department whose name is untouched skips the rename entirely: that
      // call rewrites every employee holding the old name.
      if (kind !== 'department' || edit.name !== it.name) await rename(it.id, edit.name, level);
      if (kind === 'department' && (edit.parent || '') !== (it.parent || '')) {
        await masterApi.setDepartmentParent(it.id, edit.parent || null);
      }
    }, 'Saved');
    setEdit(null);
  };
  // The edit form for one row, and the three actions that belong to it. Shared
  // by the plain list and by the by-level grouping below, so an action always
  // operates on the ONE record it is attached to however the row is drawn.
  const editorFor = (it) => (
    <div className="flex items-center gap-2 flex-1">
      <Input data-testid={`edit-${kind}-name`} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="max-w-xs" />
      {kind === 'designation' && (<Input data-testid="edit-designation-level" type="number" value={edit.level ?? ''} onChange={(e) => setEdit({ ...edit, level: e.target.value })} className="w-20" placeholder="Lvl" />)}
      {kind === 'department' && !it.children?.length && (
        <select data-testid={`edit-department-group-${it.id}`} value={edit.parent || ''}
                onChange={(e) => setEdit({ ...edit, parent: e.target.value })}
                title="Group this department belongs to"
                className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2">
          <option value="">— no group —</option>
          {groupChoices.filter((g) => g !== it.name).map((g) => <option key={g} value={g}>Inside {g}</option>)}
        </select>
      )}
      <button data-testid={`save-${kind}-${it.id}`} onClick={() => saveRow(it)} className="p-1 text-[#15A36E]"><Check className="w-4 h-4" /></button>
      <button onClick={() => setEdit(null)} className="p-1 text-[#5A6377]"><X className="w-4 h-4" /></button>
    </div>
  );

  const actionsFor = (it, compact = false) => {
    const ic = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';
    const pad = compact ? 'p-0.5' : 'p-1';
    return (
      <span className="inline-flex gap-0.5">
        <button data-testid={`rename-${kind}-${it.id}`} title={kind === 'department' ? 'Rename / move into a group' : `Rename ${it.name}`} onClick={() => setEdit({ kind, id: it.id, name: it.name, level: it.level, parent: it.parent || '' })} className={`${pad} text-[#5A6377] hover:bg-[#EFF1F6] rounded`}><Pencil className={ic} /></button>
        <button data-testid={`merge-${kind}-${it.id}`} title={`Merge ${it.name} into…`} onClick={() => openMerge(kind, it.name)} className={`${pad} text-[#15307F] hover:bg-[#E8EFFD] rounded`}><GitMerge className={ic} /></button>
        <button data-testid={`delete-${kind}-${it.id}`} title={`Delete ${it.name}`} onClick={() => wrap(() => del(it.id), 'Deleted')} className={`${pad} text-[#E11D2A] hover:bg-[#FDE7E9] rounded`}><Trash2 className={ic} /></button>
      </span>
    );
  };

  /* Designations are listed BY LEVEL — one row per rung, the roles on it
   * separated by "/". This is a DISPLAY grouping and nothing more: every name
   * in the row is its own record, keeps its own employee count, and carries
   * its own Rename / Merge / Delete. Deleting GET removes GET and leaves the
   * rest of L5 exactly as it was.
   *
   * The grouping follows the stored level, so it is the same list the rest of
   * the HRMS reads — add a role at L5 and it joins that row by itself, change
   * its level and it moves to the other row. No level is a row of its own
   * rather than being hidden, because a role nobody has levelled is something
   * HR needs to see.
   */
  // Off by default: the master is read far more often than it is edited, and
  // three buttons per role made a level unreadable.
  const [showActions, setShowActions] = useState(false);

  const byLevel = () => {
    const groups = new Map();
    items.forEach((it) => {
      const key = it.level == null || it.level === '' ? null : Number(it.level);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(it);
    });
    return [...groups.entries()]
      .map(([level, list]) => [level,
        list.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))])
      .sort((a, b) => (b[0] ?? -1) - (a[0] ?? -1));
  };

  return (
    /* One shell for every master — same padding, radius, border and shadow —
       so Units, Departments, Designations and Contractors read as one page
       rather than four widgets that grew separately. */
    <div data-testid={`section-${kind}`} className={`bg-white border border-[#E6E9F0] rounded-[14px] p-6 sm:p-7 shadow-[0_1px_2px_rgba(16,36,104,0.04)]${wide ? ' lg:col-span-2' : ''}`}>
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-[17px] font-semibold text-[#11182B] leading-tight">{title}</h2>
          <p className="text-[12px] text-[#98A1B2] mt-0.5">
            {items.length} {items.length === 1 ? 'entry' : 'entries'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-none">
          {/* Hover shows one row's actions; this pins them for every row, which
              is also how they are reached without a mouse. Off by default: the
              masters are read far more often than edited, and three buttons on
              every row left no width for the name itself. */}
          <button onClick={() => setShowActions((v) => !v)} data-testid={`${kind}-edit-toggle`}
                  title={showActions ? 'Hide the rename / merge / delete buttons'
                                     : 'Show the rename / merge / delete buttons on every row'}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[12px] font-medium border transition-colors ${
                    showActions ? 'bg-[#15307F] text-white border-[#15307F]'
                                : 'bg-white text-[#5A6377] border-[#E6E9F0] hover:bg-[#F7F8FB]'}`}>
            <Pencil className="w-3.5 h-3.5" />{showActions ? 'Done' : 'Edit'}
          </button>
          {kind === 'designation' && (
            <Button size="sm" data-testid="apply-levels-button" variant="outline" onClick={() => wrap(() => masterApi.applyLevels(), 'Levels applied to employees')}>
              <Layers className="w-4 h-4 mr-1" />Apply levels
            </Button>
          )}
        </div>
      </div>
      {/* Adding a DESIGNATION is admin-only: it extends the job ladder, and
          every new row has to be levelled, mapped to a KPI template and kept
          in the org chart for ever. Hidden here AND refused by the API
          (`master.designation_create`) — the hidden control is the courtesy,
          the server check is the rule. */}
      {canAddHere ? (
        <div className="flex gap-2 mb-5">
          <Input data-testid={`new-${kind}-input`} placeholder={`New ${kind}…`} value={newValue} onChange={(e) => setNewValue(e.target.value)} className="h-10" />
          <Button data-testid={`add-${kind}-button`} className="h-10 px-4 flex-none" onClick={async () => { const name = newValue.trim(); if (!name) return; await wrap(() => add(name), 'Added'); setNewValue(''); }}><Plus className="w-4 h-4" /></Button>
        </div>
      ) : (
        <div data-testid={`add-${kind}-restricted`}
             className="mb-5 text-[12px] text-[#98A1B2] bg-[#F7F8FB] border border-[#EDF0F5] rounded-lg px-3 py-2">
          New designations are added by an administrator. Existing ones stay
          selectable on every employee form.
        </div>
      )}
      {kind === 'designation' ? (
        <>
          <div className="flex items-start justify-between gap-3 mb-3 -mt-1">
            <p className="text-[12px] text-[#5A6377]">
              Grouped by level for reading only. Every name is still its own designation — its own
              employee count, its own rename, merge and delete — and that is what employees carry
              everywhere else in the software.
            </p>
          </div>
          <div className="divide-y divide-[#F0F2F6]">
            {byLevel().map(([level, list]) => (
              <div key={level ?? 'none'} data-testid={`level-group-${level ?? 'none'}`}
                   className="flex items-start gap-4 py-4 first:pt-1">
                {/* The badge anchors the row. Fixed width so every level lines
                    up down the left edge however many roles sit beside it. */}
                <span className="mt-0.5 flex-none inline-flex items-center justify-center w-[46px] py-1
                                 rounded-lg text-[12px] font-bold bg-[#EEF2FE] text-[#15307F]
                                 border border-[#DCE5FB] tracking-wide"
                      title={level == null ? 'No level set' : `Level ${level}`}>
                  {level == null ? '—' : `L${level}`}
                </span>
                {/* gap-y lets a crowded level wrap onto a second line without
                    the rows touching; items-center keeps chips aligned. */}
                <div className="flex flex-wrap items-center gap-y-2 min-w-0 flex-1 text-sm">
                  {list.map((it, i) => {
                    const editing = edit && edit.kind === kind && edit.id === it.id;
                    return (
                      // whitespace-nowrap keeps one designation — its name, its
                      // count and its three actions — on a single line. Without
                      // it a row broke mid-role and the group read as a column
                      // of disconnected records instead of one level.
                      <span key={it.id} className="inline-flex items-center whitespace-nowrap">
                        {/* The separator is punctuation, not a name — never read
                            out, never part of what anything is called. */}
                        {i > 0 && <span aria-hidden="true" className="text-[#D6DCE6] mx-1">/</span>}
                        {editing ? editorFor(it) : (
                          /* Three icons beside every name turned a level into a
                             wall of ironmongery. The row now reads as the list
                             of roles it is, and the actions for ONE role appear
                             when it is hovered — or stay pinned for everything
                             while "Edit" is on, which is also how they are
                             reachable without a mouse. */
                          <span data-testid={`row-${kind}-${it.id}`}
                                className="group inline-flex items-center gap-1.5 py-1 px-2 rounded-lg
                                           hover:bg-[#F1F5FD] transition-colors">
                            <button onClick={() => goToFiltered(it)} title={`View employees who are ${it.name}`}
                                    className="text-[#11182B] hover:text-[#15307F] hover:underline">
                              {it.name}
                            </button>
                            <button data-testid={`count-${kind}-${it.id}`}
                                    title={`${it.employee_count || 0} active ${it.name}`}
                                    onClick={() => goToFiltered(it)}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium
                                               bg-[#E8EFFD] text-[#15307F] hover:bg-[#D5E3FB] cursor-pointer transition-colors">
                              {it.employee_count || 0}
                            </button>
                            {/* Not rendered at all unless Edit is on. Merely
                                hiding them still reserved three icons' worth
                                of width beside every role, which is what blew
                                the names apart across the row. */}
                            {showActions && actionsFor(it, true)}
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
      <div className="divide-y divide-[#F0F2F6]">
        {items.map((it) => {
          const editing = edit && edit.kind === kind && edit.id === it.id;
          return (
            // min-w-0 on the label and flex-none on the actions. Without both,
            // a long name ("Tool Room Maintenance") refuses to shrink — a flex
            // item defaults to min-width:auto — and pushes the delete button
            // out through the right edge of the card.
            <div key={it.id} data-testid={`row-${kind}-${it.id}`}
                 className="group flex items-center gap-2 py-2 px-1 -mx-1 rounded-md hover:bg-[#F7F8FB] transition-colors">
              {editing ? editorFor(it) : (
                <>
                  <span className="text-sm text-[#11182B] flex items-center gap-2 min-w-0 flex-1" style={it.depth ? { paddingLeft: 18 * it.depth } : undefined}>
                    {it.depth ? <span className="text-[#C7CEDB] flex-none">└</span> : null}
                    <span className="truncate" title={it.name}>{it.name}</span>
                    {it.children?.length ? <span className="text-[10px] uppercase tracking-wide text-[#98A1B2] flex-none">holds {it.children.length}</span> : null}
                    {it.awaiting_reassign ? (
                      <span title="Replaced by the group above — move its people into one of the new departments, and this row disappears once it is empty"
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FBF0E6] text-[#B14A0E] flex-none">
                        reassign
                      </span>
                    ) : null}
                    {kind === 'designation' && it.level ? <span className="text-[11px] text-[#98A1B2]">L{it.level}</span> : null}
                    {(kind === 'department' || kind === 'unit' || kind === 'designation') ? (
                      <button
                        data-testid={`count-${kind}-${it.id}`}
                        title={`View employees in ${it.name}`}
                        onClick={() => goToFiltered(it)}
                        className="inline-flex flex-none items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#E8EFFD] text-[#15307F] hover:bg-[#D5E3FB] cursor-pointer transition-colors"
                      >{it.employee_count || 0}</button>
                    ) : (
                      <span data-testid={`count-${kind}-${it.id}`} title={`${it.employee_count || 0} active`} className="inline-flex flex-none items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#EFF1F6] text-[#5A6377]">{it.employee_count || 0}</span>
                    )}
                  </span>
                  <div className={`flex gap-1 flex-none transition-opacity ${
                    showActions ? 'opacity-100'
                                : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'}`}>
                    {actionsFor(it)}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

/**
 * Address each unit prints on letters.
 *
 * One place to set it: the transfer letter and anything else that names a
 * plant reads the resolved value back from the Unit master, so a correction
 * here flows through without touching any letter already issued.
 */
/* The associate transfer card — export/import of the associate bundle —
 * was removed from this screen. The endpoints and `associateBundleApi` are
 * untouched; only the UI is gone. To bring it back, restore the component and
 * its <AssociateTransferCard /> below.
 */

/* The shop-floor designation pool — the roles associates are appointed to.
 *
 * Set and seen here beside the staff designations, and managed the same way,
 * but a SEPARATE pool. The staff master carries a `level` that drives the org
 * ladder and the "apply levels" sweep; OPERATOR, HELPER and SWEEPER have no
 * level, and folding fifty of them into the staff list would put SWEEPER in
 * the picker HR appoints a manager from.
 *
 * It exists so the Associates screen can pick from a list instead of free
 * text. Typing the designation meant every misspelling became a new role —
 * the seeded pool already contains both "BIN WASHING" and "BINWASHING",
 * which is exactly what renaming one into the other here fixes.
 */
/* ------------------------------------------------------------------ */
/* The approved Associate structure, and the migration onto it.        */
/*                                                                     */
/* Two things in one card because they are one subject: what the       */
/* structure is meant to be, and how far the register still is from    */
/* it. Splitting them would let somebody read the target list and      */
/* assume the data already matches.                                    */
/* ------------------------------------------------------------------ */
const ACTION_STYLE = {
  keep:      { label: 'Already approved', cls: 'bg-[#EAF7F0] text-[#15654B] border-[#CDEBDB]' },
  merge:     { label: 'Merge',            cls: 'bg-[#EFF3FB] text-[#2F4A77] border-[#D9E2F3]' },
  champs:    { label: '→ CHAMPS (L4)',    cls: 'bg-[#FFF4E5] text-[#8A4B00] border-[#FFD9A8]' },
  remove:    { label: 'Remove',           cls: 'bg-[#FDECEC] text-[#B42318] border-[#F7CDCD]' },
  // Both shapes of "a human must decide" read the same, because the action
  // required is the same and because a row labelled like an error invites
  // somebody to clear it by guessing a destination.
  undecided: { label: 'Mapping Pending',  cls: 'bg-[#FFF7E6] text-[#8A5A12] border-[#FDD08A]' },
  unmapped:  { label: 'Mapping Pending',  cls: 'bg-[#FFF7E6] text-[#8A5A12] border-[#FDD08A]' },
};

function AssociateStructureCard() {
  const { hasPerm } = useAuth();
  const [plan, setPlan] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Decisions the approved sheet does not make, keyed by the legacy name.
  // Held in the browser until Apply, so nothing is written while the person
  // reading the list is still making up their mind.
  const [overrides, setOverrides] = useState({});

  // The pool rows, for the per-designation counts — including how many people
  // hold a role with NO level yet, which is the number that matters while the
  // Skill Matrix has scored nobody.
  const [pool, setPool] = useState([]);

  const allowed = hasPerm('master.manage');
  const load = () => {
    associateDesignationApi.migrationPreview()
      .then((r) => setPlan(r.data)).catch(() => setPlan(null));
    associateDesignationApi.list()
      .then((r) => setPool(r.data || [])).catch(() => setPool([]));
  };
  useEffect(() => { if (allowed) load(); }, [allowed]);
  if (!allowed || !plan) return null;

  const approved = plan.approved || [];
  const blocking = plan.blocking || [];
  const resolved = blocking.filter((b) => overrides[b.from]);
  const stillBlocking = blocking.length - resolved.length;

  const apply = async () => {
    if (!window.confirm(
      `Migrate ${plan.totals.associates_moved} associate record(s) onto the approved structure?\n\n`
      + 'Nobody is deleted and no history is lost — people are moved and the old '
      + 'designations are deactivated carrying what they were merged into.\n\n'
      + 'DESIGNATION ONLY. No skill level is assigned: L1/L2/L3 come from the Skill '
      + 'Matrix, so every level stays "—" until an assessment result exists.')) return;
    setBusy(true);
    try {
      const r = await associateDesignationApi.migrate(true, overrides);
      toast.success(`${r.data.associates_moved} associate(s) migrated; `
                    + `${r.data.designations_retired.length} designation(s) retired`);
      setOverrides({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Migration failed'); }
    finally { setBusy(false); }
  };

  /* One level, one group. The "/" list is a DISPLAY device for this card only:
     internally these are thirteen separate records and an associate holds
     exactly one of them — nobody is ever filed as "OPERATOR / ELECTRICIAN".
     The level is a badge on the left, the designations flow as one block on
     the right and wrap inside it, and each name stays its own clickable thing.

     `level` is the number the counts are read at. A count is shown only where
     somebody is actually recorded at that level, so three rows of "(0)" do not
     imply the ladder is populated when nobody has been assessed yet. */
  const countAt = (name, level) => {
    const row = pool.find((p) => p.name === name);
    return Number(row?.count_by_level?.[String(level)] || 0);
  };
  const levelRow = (label, note, names, level) => (
    <div className="flex items-start gap-3 py-2.5 border-b border-[#F2F4F8] last:border-0"
         data-testid={`assoc-level-row-${label}`}>
      <span className="mt-0.5 flex-none inline-flex items-center justify-center min-w-[34px] px-2 py-0.5
                       rounded-md text-[11px] font-bold bg-[#E8EFFD] text-[#15307F]">
        {label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center text-sm">
          {names.map((n, i) => {
            const c = countAt(n, level);
            return (
              /* nowrap per designation: a role never splits across two lines,
                 so the row reads as one level rather than a broken column. */
              <span key={n} className="inline-flex items-center whitespace-nowrap">
                {i > 0 && <span aria-hidden="true" className="text-[#C9CFDB] mx-1.5">/</span>}
                <button onClick={() => navigate(`/associates?designation=${encodeURIComponent(n)}`)}
                        title={c ? `${c} associate(s) assessed at ${label}` : `View ${n} on the register`}
                        className="text-[#11182B] hover:text-[#15307F] hover:underline">
                  {n}
                </button>
                {c > 0 && (
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px]
                                   font-medium bg-[#E8EFFD] text-[#15307F]">{c}</span>
                )}
              </span>
            );
          })}
        </div>
        {note && <div className="text-xs text-[#98A1B2] mt-1">{note}</div>}
      </div>
    </div>
  );

  return (
    <div data-testid="associate-structure-card"
         className="bg-white border border-[#E6E9F0] rounded-[14px] p-6 mt-6">
      <h2 className="text-lg font-medium text-[#11182B] mb-1">Associate structure (L1–L4)</h2>
      <p className="text-sm text-[#5A6377] mb-4">
        The {approved.length} approved Associate designations. Each one is valid at L1, L2 and L3 —
        the designation is the job, the level is the proficiency, and the Skill Matrix is what
        moves somebody between them. L4 is {plan.champs}. Staff levels L5–L15 are a separate
        master and are not touched here.
      </p>

      <div className="border border-[#E6E9F0] rounded-[10px] px-4 py-1 mb-3">
        {levelRow('L1', 'Entry proficiency', approved, 1)}
        {levelRow('L2', 'Same thirteen designations, higher proficiency', approved, 2)}
        {levelRow('L3', 'Same thirteen designations, highest skill level', approved, 3)}
        {levelRow('L4', 'Supervisor and Team Leader are represented here', [plan.champs], 4)}
      </div>

      {/* Where everybody actually is. The three rows above are the STRUCTURE;
          this is the register, and until the Skill Matrix has scored somebody
          their level is pending — which the employee table shows as "—". It is
          stated here so nobody reads the L1 row as a list of L1 employees. */}
      {plan.totals?.levels_pending > 0 && (
        <div data-testid="assoc-level-pending-note"
             className="flex items-start gap-3 border border-[#FDD08A] bg-[#FFF7E6] rounded-[10px] px-4 py-2.5 mb-5">
          <span className="mt-0.5 flex-none inline-flex items-center justify-center min-w-[34px] px-2 py-0.5
                           rounded-md text-[11px] font-bold bg-white text-[#8A5A12] border border-[#FDD08A]">
            —
          </span>
          <div className="text-xs text-[#8A5A12]">
            <b>{plan.totals.levels_pending} associates have no level yet.</b> Level comes from the
            Skill Matrix assessment and from nothing else, so it stays <b>—</b> — not L1, not zero —
            until a result exists. Their designation is recorded and in use; only the level is pending.
          </div>
        </div>
      )}

      {/* The sheet's braces. Grouped for reading only — expanding one shows
          the separate designations it actually contains. */}
      {(plan.groups || []).map((g) => (
        <details key={g.key} className="mb-2 text-sm">
          <summary className="cursor-pointer text-[#2563EB]">{g.label}</summary>
          <div className="pl-4 pt-1 text-[#5A6377]">
            {g.members.map((m) => <div key={m}>{m} — a designation in its own right</div>)}
            {/* Shop-floor words that are NOT designations. The work keeps its
                name here while the person is filed under an approved one. */}
            {(g.sub_roles || []).map((s) => (
              <div key={s.name} className="text-[#98A1B2]">
                {s.name} — filed as {s.filed_as}
              </div>
            ))}
          </div>
        </details>
      ))}

      <div className="mt-5 pt-5 border-t border-[#F2F4F8]">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-[#11182B]">Migration from the legacy pool</div>
            <div className="text-sm text-[#5A6377]">
              {plan.totals.designations_now} designations in the pool today ·{' '}
              {plan.totals.associates_moved} of {plan.totals.associates} associate records would move.
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}
                  data-testid="assoc-migration-toggle">
            {open ? 'Hide' : 'Review'}
          </Button>
        </div>

        {stillBlocking > 0 && (
          <div data-testid="assoc-migration-blocked"
               className="mt-3 text-sm bg-[#FFF7E6] border border-[#FDD08A] text-[#8A5A12] rounded-[10px] px-3 py-2">
            <strong>Mapping Pending — {stillBlocking} designation(s)</strong> covering{' '}
            {blocking.filter((b) => !overrides[b.from]).reduce((s, b) => s + b.total, 0)} people are
            not placed by the approved sheet. Migration is blocked until each one is decided here —
            guessing a destination would silently refile a real person.
          </div>
        )}

        {open && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F8FB] text-[#5A6377] text-xs uppercase">
                <tr>
                  <th className="text-left py-2 px-2">Current designation</th>
                  <th className="text-right py-2 px-2 w-24">Records</th>
                  <th className="text-left py-2 px-2 w-44">Action</th>
                  <th className="text-left py-2 px-2">Becomes</th>
                </tr>
              </thead>
              <tbody>
                {plan.rows.map((r) => {
                  const chosen = overrides[r.from];
                  const style = ACTION_STYLE[chosen ? 'merge' : r.action] || ACTION_STYLE.merge;
                  const needsDecision = ['unmapped', 'undecided', 'remove'].includes(r.action) && r.total > 0;
                  return (
                    <tr key={r.from} className="border-b border-[#F2F4F8]">
                      <td className="py-1.5 px-2 text-[#11182B]">{r.from}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-[#5A6377]">
                        {r.total || <span className="text-[#C9CFDC]">—</span>}
                      </td>
                      <td className="py-1.5 px-2">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${style.cls}`}>
                          {chosen ? 'Decided here' : style.label}
                        </span>
                      </td>
                      <td className="py-1.5 px-2 text-[#5A6377]">
                        {needsDecision ? (
                          /* Only approved destinations are offerable, so a
                             decision made here cannot invent a fourteenth
                             designation. */
                          <select
                            value={chosen || ''}
                            onChange={(e) => setOverrides((p) => {
                              const next = { ...p };
                              if (e.target.value) next[r.from] = e.target.value;
                              else delete next[r.from];
                              return next;
                            })}
                            className="border border-[#E6E9F0] rounded-lg px-2 py-1 text-sm"
                            data-testid={`assoc-migration-pick-${r.from}`}>
                            <option value="">Decide…</option>
                            {[...approved, plan.champs].map((a) => <option key={a} value={a}>{a}</option>)}
                          </select>
                        ) : (r.to || <span className="text-[#C9CFDC]">—</span>)}
                        {r.why && <div className="text-xs text-[#98A1B2] mt-0.5">{r.why}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="flex items-center gap-3 mt-4">
              <Button size="sm" onClick={apply} disabled={busy || stillBlocking > 0}
                      data-testid="assoc-migration-apply">
                Apply migration
              </Button>
              <span className="text-xs text-[#98A1B2]">
                {stillBlocking > 0
                  ? `Decide the remaining ${stillBlocking} to enable this.`
                  : 'Moves designations, retires the legacy ones, deletes nobody — '
                    + 'and assigns no skill level.'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AssociateDesignationCard() {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);          // { id, name }
  const [showAll, setShowAll] = useState(false);
  const [merging, setMerging] = useState(null);    // the row being folded away
  const [mergeInto, setMergeInto] = useState('');

  const load = () => associateDesignationApi.list()
    .then((r) => setRows(r.data || []))
    .catch(() => setRows([]));
  const allowed = hasPerm('master.manage');
  useEffect(() => { if (allowed) load(); }, [allowed]);
  if (!allowed) return null;

  const add = async () => {
    if (!name.trim()) { toast.error('Designation is required'); return; }
    setBusy(true);
    try {
      await associateDesignationApi.add(name);
      toast.success(`${name.trim().toUpperCase()} added to the pool`);
      setName(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };
  const save = async () => {
    try {
      await associateDesignationApi.update(edit.id, { name: edit.name });
      toast.success('Renamed — every associate holding it was moved across');
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };
  const toggle = async (d) => {
    try {
      await associateDesignationApi.update(d.id, { active: !d.active });
      toast.success(d.active ? `${d.name} hidden from the picker` : `${d.name} back in the picker`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change'); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.name}?\n\nOnly possible while no associate record holds it, including people who have left.`)) return;
    try { await associateDesignationApi.remove(d.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };
  const doMergeDesig = async () => {
    if (!mergeInto) { toast.error('Pick what to merge it into'); return; }
    try {
      const r = await associateDesignationApi.merge(merging.name, mergeInto);
      toast.success(`${r.data.moved} associate(s) moved to ${mergeInto}`);
      setMerging(null); setMergeInto(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not merge'); }
  };

  const shown = showAll ? rows : (rows || []).slice(0, 12);
  const unused = (rows || []).filter((d) => !d.associate_count).length;

  return (
    <div data-testid="associate-designation-card" className="bg-white border border-[#E6E9F0] rounded-[14px] p-6 mt-6">
      <h2 className="text-lg font-medium text-[#11182B] mb-1">Associate designations</h2>
      <p className="text-sm text-[#5A6377] mb-4">
        The pool the Associates screen picks from, shared across all units — kept apart from the
        staff designations above, which carry a level and drive the org ladder. Renaming one here
        moves every associate holding it. &ldquo;Inspector&rdquo; is stored as
        &ldquo;Checker&rdquo;, which is the approved wording.
        {unused > 0 && ` ${unused} of these are held by nobody right now.`}
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-5 pb-5 border-b border-[#F2F4F8]">
        <div>
          <Label className="text-xs">Designation</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                 placeholder="e.g. GRINDING OPERATOR" className="w-72"
                 data-testid="assoc-desig-add-name" />
        </div>
        <Button size="sm" onClick={add} disabled={busy} data-testid="assoc-desig-add">
          <Plus className="w-3.5 h-3.5 mr-1" />Add designation
        </Button>
      </div>

      {!rows ? <p className="text-sm text-[#98A1B2]">Loading…</p>
        : rows.length === 0 ? <p className="text-sm text-[#98A1B2]">The pool is empty.</p> : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
                <tr>
                  <th className="py-2 px-2 text-left">Designation</th>
                  <th className="py-2 px-2 text-right w-28">Associates</th>
                  <th className="py-2 px-2 text-left w-24">In picker</th>
                  <th className="py-2 px-2 w-20" />
                </tr>
              </thead>
              <tbody>
                {shown.map((d) => (
                  <tr key={d.id} className="border-b border-[#F2F4F8]" data-testid={`assoc-desig-${d.id}`}>
                    <td className="py-2 px-2 font-medium text-[#11182B]">
                      {edit?.id === d.id ? (
                        <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                               onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEdit(null); }}
                               className="h-8" autoFocus />
                      ) : d.name}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums font-semibold text-[#172554]">
                      {d.associate_count}
                    </td>
                    <td className="py-2 px-2">
                      <button onClick={() => toggle(d)}
                              className={`text-xs px-2 py-0.5 rounded-full border ${d.active
                                ? 'bg-[#EAF7F0] text-[#15654B] border-[#CDEBDB]'
                                : 'bg-[#F2F4F8] text-[#98A1B2] border-[#E6E9F0]'}`}>
                        {d.active ? 'shown' : 'hidden'}
                      </button>
                    </td>
                    <td className="py-2 px-2 text-right whitespace-nowrap">
                      {edit?.id === d.id ? (
                        <>
                          <button onClick={save} title="Save" className="text-[#15654B] px-1"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEdit(null)} title="Cancel" className="text-[#98A1B2] px-1"><X className="w-4 h-4" /></button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => setEdit({ id: d.id, name: d.name })} title="Rename"
                                  className="text-[#5A6377] hover:text-[#2563EB] px-1"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => { setMerging(d); setMergeInto(''); }}
                                  title="Merge into another designation"
                                  data-testid={`assoc-desig-merge-${d.id}`}
                                  className="text-[#5A6377] hover:text-[#2563EB] px-1"><GitMerge className="w-3.5 h-3.5" /></button>
                          <button onClick={() => remove(d)} title="Delete"
                                  className="text-[#C2151F] hover:opacity-70 px-1"><Trash2 className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 12 && (
            <button onClick={() => setShowAll((v) => !v)}
                    className="mt-3 text-xs text-[#2563EB] hover:underline"
                    data-testid="assoc-desig-toggle-all">
              {showAll ? 'Show fewer' : `Show all ${rows.length}`}
            </button>
          )}

          <Dialog open={!!merging} onOpenChange={(o) => { if (!o) setMerging(null); }}>
            <DialogContent>
              <DialogHeader><DialogTitle>Merge designation</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-[#5A6377]">
                  Move every associate on <span className="font-semibold text-[#11182B]">{merging?.name}</span>
                  {' '}({merging?.associate_count || 0}) onto another designation, then remove it from the
                  pool. Use this for two spellings of one job — renaming cannot do it, because the
                  name it would take is already taken. This can&rsquo;t be undone.
                </p>
                <div>
                  <Label>Merge into</Label>
                  <select data-testid="assoc-desig-merge-target"
                          className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                          value={mergeInto} onChange={(e) => setMergeInto(e.target.value)}>
                    <option value="">— select target —</option>
                    {(rows || []).filter((x) => x.name !== merging?.name)
                      .map((x) => <option key={x.id} value={x.name}>{x.name}</option>)}
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMerging(null)}>Cancel</Button>
                <Button data-testid="assoc-desig-confirm-merge" onClick={doMergeDesig}
                        disabled={!mergeInto}>Merge</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

/* The labour suppliers the plants draw associates from.
 *
 * A master like units and departments, and here for the same reason: adding
 * one declares a new company the business may take people from, and every
 * associate filed under it inherits that. The Associates screen shows the
 * same list read-only, so a plant can see what a contractor costs it without
 * being able to invent one.
 *
 * Admin only — `associate.contractor` is granted to no stored role, and admin
 * reaches it through FULL_ACCESS_ROLES. The card hides itself for anybody
 * else rather than showing buttons the server will refuse.
 */
function ContractorMasterCard() {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState(null);
  const [name, setName] = useState('');
  const [short, setShort] = useState('');
  const [ownPayroll, setOwnPayroll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);     // { id, name, short_name }

  const load = () => contractorApi.list()
    .then((r) => setRows(r.data || []))
    .catch(() => setRows([]));
  // A boolean, not `hasPerm` itself — the context rebuilds that function on
  // every render, so depending on it would re-fetch the list each time
  // anything in AuthProvider changed.
  const allowed = hasPerm('associate.contractor');
  useEffect(() => { if (allowed) load(); }, [allowed]);

  if (!allowed) return null;

  const add = async () => {
    if (!name.trim()) { toast.error('Contractor name is required'); return; }
    setBusy(true);
    try {
      await contractorApi.add({ name, short_name: short || undefined, is_own_payroll: ownPayroll });
      toast.success(`${name.trim()} added`);
      setName(''); setShort(''); setOwnPayroll(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };
  const save = async () => {
    try {
      await contractorApi.update(edit.id, { name: edit.name, short_name: edit.short_name });
      toast.success('Saved'); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?\n\nOnly possible while it holds no associate records at all, including people who have left.`)) return;
    try { await contractorApi.remove(c.id); toast.success(`${c.name} deleted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  return (
    <div data-testid="contractor-master-card" className="bg-white border border-[#E6E9F0] rounded-[14px] p-6 mt-6">
      <h2 className="text-lg font-medium text-[#11182B] mb-1">Labour contractors</h2>
      <p className="text-sm text-[#5A6377] mb-4">
        The suppliers the plants draw <b>associates</b> from, shared across all units. Associates
        are added on the Associates screen; the contractors themselves are declared here.
        A contractor that still holds records cannot be deleted — rename it, or leave it in place.
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-5 pb-5 border-b border-[#F2F4F8]">
        <div>
          <Label className="text-xs">Contractor name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                 placeholder="e.g. Shree Manpower Services" className="w-72"
                 data-testid="contractor-add-name" />
        </div>
        <div>
          <Label className="text-xs">Short name (optional)</Label>
          <Input value={short} onChange={(e) => setShort(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                 placeholder="e.g. Shree" className="w-40" />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#5A6377] pb-2"
               title="Tick for the company's own workers rather than contract labour">
          <input type="checkbox" checked={ownPayroll} onChange={(e) => setOwnPayroll(e.target.checked)}
                 data-testid="contractor-own-payroll" />
          Own payroll
        </label>
        <Button size="sm" onClick={add} disabled={busy} data-testid="contractor-add">
          <Plus className="w-3.5 h-3.5 mr-1" />Add contractor
        </Button>
      </div>

      {!rows ? <p className="text-sm text-[#98A1B2]">Loading…</p>
        : rows.length === 0 ? <p className="text-sm text-[#98A1B2]">No contractors yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
              <tr>
                <th className="py-2 px-2 text-left">Contractor</th>
                <th className="py-2 px-2 text-left w-40">Short name</th>
                <th className="py-2 px-2 text-right w-28">Associates</th>
                <th className="py-2 px-2 w-20" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-[#F2F4F8]" data-testid={`contractor-${c.id}`}>
                  {edit?.id === c.id ? (
                    <>
                      <td className="py-1.5 px-2">
                        <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                               onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEdit(null); }}
                               className="h-8" autoFocus />
                      </td>
                      <td className="py-1.5 px-2">
                        <Input value={edit.short_name || ''} onChange={(e) => setEdit({ ...edit, short_name: e.target.value })}
                               onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEdit(null); }}
                               className="h-8" />
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{c.headcount}</td>
                      <td className="py-1.5 px-2 text-right whitespace-nowrap">
                        <button onClick={save} title="Save" className="text-[#15654B] px-1"><Check className="w-4 h-4" /></button>
                        <button onClick={() => setEdit(null)} title="Cancel" className="text-[#98A1B2] px-1"><X className="w-4 h-4" /></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-2 px-2 font-medium text-[#11182B]">
                        {c.name}
                        {c.is_own_payroll && (
                          <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#EAF7F0] text-[#15654B] border border-[#CDEBDB]">
                            own payroll
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-[#5A6377]">{c.short_name}</td>
                      <td className="py-2 px-2 text-right tabular-nums font-semibold text-[#172554]">{c.headcount}</td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <button onClick={() => setEdit({ id: c.id, name: c.name, short_name: c.short_name })}
                                title="Rename" className="text-[#5A6377] hover:text-[#2563EB] px-1">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(c)} title="Delete"
                                className="text-[#C2151F] hover:opacity-70 px-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* Which grades the organisational CHART draws.
 *
 * The HRMS hierarchy and the published chart are two different things. The
 * company runs L1–L15 and every one of those levels stays live in the employee
 * record, Attendance, Late Coming, Leave, Payroll, Reports and the Employee
 * Master. The chart is a document about the management structure, and the
 * junior grades are left off it — L5–L15 drawn, L1–L4 not.
 *
 * This card changes a VIEW setting and nothing else. No level is deleted, no
 * designation is edited and no employee record is touched: turning a level
 * back on makes its people reappear on the chart at once, because their data
 * never moved.
 */
function OrgChartLevelsCard() {
  const [state, setState] = useState(null);     // { all_levels, visible, hidden, default_hidden }
  const [busy, setBusy] = useState(false);

  const load = () => orgchartApi.levelVisibility()
    .then((r) => setState(r.data))
    .catch(() => setState(null));
  useEffect(() => { load(); }, []);

  const save = async (visible) => {
    if (!visible.length) { toast.error('At least one level must stay on the chart.'); return; }
    setBusy(true);
    try {
      const r = await orgchartApi.setLevelVisibility({ visible });
      setState((s) => ({ ...s, ...r.data }));
      toast.success('Org chart levels updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  if (!state) return null;
  const visible = new Set(state.visible || []);
  const toggle = (lv) => {
    const next = new Set(visible);
    if (next.has(lv)) next.delete(lv); else next.add(lv);
    save([...next].sort((a, b) => a - b));
  };
  const isDefault = String(state.hidden) === String(state.default_hidden);

  return (
    <div data-testid="orgchart-levels-card" className="bg-white border border-[#E6E9F0] rounded-[14px] p-6 mt-6">
      <h2 className="text-lg font-medium text-[#11182B] mb-1">Organisational chart levels</h2>
      <p className="text-sm text-[#5A6377] mb-4">
        Which grades the <b>Org Chart</b> draws. This is a display setting for the chart alone —
        every level stays in the HRMS and keeps working in Attendance, Late Coming, Leave, Payroll,
        Reports and the Employee Master. Nothing is deleted or deactivated, so switching a level
        back on puts its people straight back on the chart.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        {(state.all_levels || []).map((lv) => {
          const on = visible.has(lv);
          return (
            <button key={lv} type="button" disabled={busy}
              data-testid={`orgchart-level-${lv}`} aria-pressed={on}
              onClick={() => toggle(lv)}
              title={on ? `L${lv} is drawn on the chart — click to leave it off`
                        : `L${lv} is not drawn on the chart — click to include it`}
              className={`px-3 py-1.5 rounded-full text-[13px] font-medium border transition-colors ${
                on ? 'bg-[#15307F] text-white border-[#15307F]'
                   : 'bg-white text-[#98A1B2] border-[#E6E9F0] line-through hover:bg-[#F7F8FB]'}`}>
              L{lv}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[12px] text-[#5A6377]">
        <span data-testid="orgchart-levels-summary">
          On the chart: <b className="text-[#11182B]">{(state.visible || []).map((l) => `L${l}`).join(', ') || '—'}</b>
          {(state.hidden || []).length
            ? <> · Not on the chart: <b className="text-[#11182B]">{state.hidden.map((l) => `L${l}`).join(', ')}</b></>
            : null}
        </span>
        {!isDefault && (
          <Button size="sm" variant="outline" disabled={busy}
            data-testid="orgchart-levels-reset"
            onClick={() => save((state.all_levels || []).filter((lv) => !(state.default_hidden || []).includes(lv)))}>
            Reset to L5–L15
          </Button>
        )}
      </div>
    </div>
  );
}

function UnitLetterheadCard() {
  const [units, setUnits] = useState([]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState('');

  const load = () => masterApi.units()
    .then((r) => {
      const list = r.data || [];
      setUnits(list);
      setDraft(Object.fromEntries(list.map((u) => [u.id, u.letterhead?.address || ''])));
    })
    .catch(() => setUnits([]));
  useEffect(() => { load(); }, []);

  const save = async (u) => {
    setBusy(u.id);
    try {
      await masterApi.setUnitLetterhead(u.id, { letter_address: draft[u.id] ?? '' });
      toast.success(`Address saved for ${u.name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };

  return (
    <div data-testid="unit-letterhead-card" className="bg-white border border-[#E6E9F0] rounded-[14px] p-6 mt-6">
      <h2 className="text-lg font-medium text-[#11182B] mb-1">Unit addresses on letters</h2>
      <p className="text-sm text-[#5A6377] mb-4">
        How each unit is written inside a letter — for example on a transfer letter. Leave blank to
        print the unit name alone.
      </p>
      <div className="space-y-3">
        {units.map((u) => (
          <div key={u.id} data-testid={`unit-letterhead-${u.id}`} className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-[#11182B] w-[190px] flex-shrink-0 truncate">{u.name}</span>
            <Input
              value={draft[u.id] ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, [u.id]: e.target.value }))}
              placeholder="Plot No-79, Sec-68 IMT Faridabad"
              className="flex-1 min-w-[240px]"
            />
            <Button size="sm" variant="outline" disabled={busy === u.id}
                    onClick={() => save(u)}>Save</Button>
          </div>
        ))}
        {units.length === 0 && <p className="text-sm text-[#98A1B2]">No units in the master yet.</p>}
      </div>
    </div>
  );
}

function LetterSignatureCard() {
  const [sig, setSig] = useState(null);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await signatureApi.get();
      const v = r.data && r.data.url ? r.data : null;
      setSig(v);
      if (v?.name) setName(v.name);
      if (v?.title) setTitle(v.title);
      if (v?.signatory_email) setEmail(v.signatory_email);
    } catch { setSig(null); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!name.trim()) { toast.error('Enter the signatory name first'); return; }
    if (!email.trim()) { toast.error('Enter the signatory email — approval codes are sent there'); return; }
    setBusy(true);
    try {
      await signatureApi.upload(file, name.trim(), title.trim(), email.trim());
      toast.success('Signature saved — it will appear on generated letters');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remove the signature? Letters fall back to a blank signing line.')) return;
    try { await signatureApi.remove(); toast.success('Signature removed'); await load(); }
    catch { toast.error('Could not remove'); }
  };

  return (
    <div className="mt-6 bg-white border border-[#E6E9F0] rounded-xl p-5" data-testid="letter-signature-card">
      <h2 className="font-display text-lg font-bold tracking-[-0.015em] text-[#172554] mb-1">Letter Signature</h2>
      <p className="text-[11px] text-[#98A1B2] mb-4">
        Scanned signature printed above the signatory&apos;s name on offer, confirmation and other
        generated letters. Background removal and cropping happen automatically on upload.
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs font-semibold text-[#5A6377] mb-1 block">Signatory name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="ANJU RAVISH"
            className="w-full border border-[#E6E9F0] rounded-md text-sm px-3 py-2" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5A6377] mb-1 block">Designation</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="MANAGER"
            className="w-full border border-[#E6E9F0] rounded-md text-sm px-3 py-2" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#5A6377] mb-1 block">
            Signatory email <span className="font-normal text-[#98A1B2]">— approval codes are sent here before any signed letter can print</span>
          </label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="anju.admin@aampl.in"
            className="w-full border border-[#E6E9F0] rounded-md text-sm px-3 py-2" />
        </div>
      </div>
      {sig?.url && (
        <div className="border border-[#E6E9F0] rounded-lg p-4 mb-3 bg-[#FBFCFE]">
          <img src={resolveMediaUrl(sig.url)} alt="Signature" style={{ height: 56, objectFit: 'contain' }} />
          <div className="border-t border-[#11182B] w-56 mt-1" />
          <div className="text-sm font-semibold text-[#11182B] mt-1">{sig.name}</div>
          <div className="text-xs text-[#5A6377]">({sig.title})</div>
          <div className="text-[10px] text-[#98A1B2] mt-2">
            Uploaded by {sig.uploaded_by} · {(sig.uploaded_at || '').slice(0, 10)}
            {sig.signatory_email ? ` · approvals to ${sig.signatory_email}` : ''}
          </div>
        </div>
      )}
      <div className="flex items-center gap-3">
        <label className="inline-block">
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            data-testid="signature-file"
            onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
          <span className="inline-block bg-[#2563EB] text-white text-sm rounded-md px-4 py-2 cursor-pointer">
            {busy ? 'Uploading…' : sig ? 'Replace signature' : 'Upload signature'}
          </span>
        </label>
        {sig && <button onClick={remove} className="text-sm text-[#C2151F] hover:underline">Remove</button>}
      </div>
    </div>
  );
}

function DocReqCard() {
  const [reqs, setReqs] = useState([]);
  const [name, setName] = useState('');
  const [newMand, setNewMand] = useState(false);
  const load = () => docReqApi.list().then((r) => setReqs(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim()) return;
    try { await docReqApi.add(name.trim(), newMand); setName(''); setNewMand(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };
  const toggle = async (r) => { try { await docReqApi.update(r.id, { mandatory: !r.mandatory }); load(); } catch (e) { toast.error('Update failed'); } };
  const remove = async (r) => { if (!window.confirm(`Remove "${r.name}"?`)) return; try { await docReqApi.remove(r.id); load(); } catch (e) { toast.error('Remove failed'); } };
  return (
    <div className="mt-6 bg-white border border-[#E6E9F0] rounded-xl p-5">
      <h2 className="font-display text-lg font-bold tracking-[-0.015em] text-[#172554] mb-1">Joining Document Requirements</h2>
      <p className="text-sm text-[#5A6377] mb-4">Tick <span className="font-medium">Mandatory</span> to require a document when a new employee is added.</p>
      <div className="space-y-2 mb-4">
        {reqs.map((r) => (
          <div key={r.id} className="flex items-center gap-3 text-sm py-1 border-b border-[#F2F4F8] last:border-0">
            <span className="flex-1 text-[#11182B]">{r.name}</span>
            <label className="flex items-center gap-1.5 text-xs text-[#5A6377] cursor-pointer">
              <input type="checkbox" checked={!!r.mandatory} onChange={() => toggle(r)} /> Mandatory
            </label>
            <button onClick={() => remove(r)} className="text-[#C2151F] text-xs">Remove</button>
          </div>
        ))}
        {reqs.length === 0 && <p className="text-xs text-[#98A1B2]">Loading…</p>}
      </div>
      <div className="flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New document name" className="max-w-xs" />
        <label className="flex items-center gap-1.5 text-xs text-[#5A6377] cursor-pointer"><input type="checkbox" checked={newMand} onChange={(e) => setNewMand(e.target.checked)} /> Mandatory</label>
        <Button size="sm" onClick={add}>Add</Button>
      </div>
    </div>
  );
}

const SCOPE_SUFFIX = { all: 'all units', unit: 'one unit', unit_department: 'one unit + dept' };
const roleLabel = (v, list) => {
  const r = (list || []).find((x) => x.role === v);
  return r ? `${r.role} — ${SCOPE_SUFFIX[r.scope] || r.scope}` : v;
};
const blankUser = { email: '', full_name: '', role: 'viewer', unit: '', department: '', password: '', active: true };

function UserAdminCard() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [depts, setDepts] = useState([]);
  const [roleDefs, setRoleDefs] = useState([]);
  const [dlg, setDlg] = useState(null);          // { mode: 'create'|'edit', form }
  const [busy, setBusy] = useState(false);
  const [pwFor, setPwFor] = useState(null);       // user being reset
  const [pwVal, setPwVal] = useState('');

  const isAdmin = user?.role === 'admin' || (user?.permissions || []).includes('rbac.manage');
  const load = async () => {
    try {
      const [u, un, dp] = await Promise.all([userApi.list(), masterApi.units(), masterApi.departments()]);
      setUsers(u.data); setUnits(un.data); setDepts(dp.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load users'); }
    rbacApi.roles().then((r) => setRoleDefs(r.data || [])).catch(() => setRoleDefs([]));
  };
  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [isAdmin]);
  if (!isAdmin) return null;

  const f = dlg?.form || blankUser;
  const setF = (k, v) => setDlg((d) => ({ ...d, form: { ...d.form, [k]: v } }));
  const selScope = (roleDefs.find((r) => r.role === f.role) || {}).scope || 'all';
  const needsUnit = selScope === 'unit' || selScope === 'unit_department';
  const needsDept = selScope === 'unit_department';

  const save = async () => {
    if (dlg.mode === 'create' && (!f.email || !f.password)) { toast.error('Email and password are required'); return; }
    if (dlg.mode === 'create' && f.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (needsUnit && !f.unit) { toast.error('This role requires a unit'); return; }
    if (needsDept && !f.department) { toast.error('This role requires a department'); return; }
    setBusy(true);
    try {
      if (dlg.mode === 'create') {
        await userApi.create({ email: f.email, password: f.password, full_name: f.full_name, role: f.role, unit: f.unit || null, department: f.department || null });
        toast.success('User created');
      } else {
        await userApi.update(dlg.form.id, { full_name: f.full_name, role: f.role, unit: f.unit || null, department: f.department || null, active: f.active });
        toast.success('User updated');
      }
      setDlg(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const doReset = async () => {
    if (pwVal.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    try { await userApi.resetPassword(pwFor.id, pwVal); toast.success('Password reset'); setPwFor(null); setPwVal(''); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try { await userApi.remove(u.id); toast.success('User deleted'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="user-admin-card" className="bg-white border border-[#E6E9F0] rounded-[14px] p-6 mt-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold text-[#11182B]">Users &amp; Access</h2>
        <Button data-testid="add-user-button" onClick={() => setDlg({ mode: 'create', form: { ...blankUser } })}><Plus className="w-4 h-4 mr-1" />Add user</Button>
      </div>
      <p className="text-sm text-[#5A6377] mb-4">Login accounts and their roles. Email is the login ID. Only admins can manage users.</p>
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase text-[#98A1B2] border-b border-[#E6E9F0]">
              <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Email (login ID)</th><th className="py-2 pr-3">Role</th>
              <th className="py-2 pr-3">Scope</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#F0F2F6]">
                <td className="py-2 pr-3 font-medium text-[#11182B]">{u.full_name || '—'}{u.id === user.id && <span className="ml-1 text-[10px] text-[#2563EB]">(you)</span>}</td>
                <td className="py-2 pr-3 text-[#5A6377]">{u.email}</td>
                <td className="py-2 pr-3">{roleLabel(u.role, roleDefs)}</td>
                <td className="py-2 pr-3 text-[#5A6377]">{[u.unit, u.department].filter(Boolean).join(' · ') || '—'}</td>
                <td className="py-2 pr-3">{u.active === false ? <span className="text-[#C2151F]">Inactive</span> : <span className="text-[#15A36E]">Active</span>}</td>
                <td className="py-2 pr-3 text-right whitespace-nowrap">
                  <button className="text-[#2563EB] hover:underline mr-3" onClick={() => setDlg({ mode: 'edit', form: { ...blankUser, ...u, password: '' } })}>Edit</button>
                  <button className="text-[#5A6377] hover:underline mr-3" onClick={() => { setPwFor(u); setPwVal(''); }}>Reset PW</button>
                  <button className="text-[#C2151F] hover:underline disabled:opacity-40" disabled={u.id === user.id} onClick={() => del(u)}>Delete</button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-[#98A1B2]">No users.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => { if (!o) setDlg(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dlg?.mode === 'create' ? 'Add user' : 'Edit user'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full name</Label><Input data-testid="user-name" value={f.full_name} onChange={(e) => setF('full_name', e.target.value)} /></div>
            {dlg?.mode === 'create' ? (
              <>
                <div><Label>Email (login ID)</Label><Input data-testid="user-email" type="email" value={f.email} onChange={(e) => setF('email', e.target.value)} /></div>
                <div><Label>Temporary password</Label><Input data-testid="user-password" type="text" value={f.password} onChange={(e) => setF('password', e.target.value)} placeholder="min 8 characters" /></div>
              </>
            ) : (
              <div><Label>Email (login ID)</Label><Input value={f.email} disabled className="bg-[#F7F8FB]" /></div>
            )}
            <div>
              <Label>Role</Label>
              <select data-testid="user-role" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full" value={f.role} onChange={(e) => setF('role', e.target.value)}>
                {roleDefs.map((r) => <option key={r.role} value={r.role}>{roleLabel(r.role, roleDefs)}</option>)}
              </select>
            </div>
            {needsUnit && (
              <div>
                <Label>Unit</Label>
                <select data-testid="user-unit" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full" value={f.unit || ''} onChange={(e) => setF('unit', e.target.value)}>
                  <option value="">— select —</option>{units.map((x) => <option key={x.id} value={x.name}>{x.name}</option>)}
                </select>
              </div>
            )}
            {needsDept && (
              <div>
                <Label>Department</Label>
                <select data-testid="user-dept" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full" value={f.department || ''} onChange={(e) => setF('department', e.target.value)}>
                  <option value="">— select —</option>{depts.map((x) => <option key={x.id} value={x.name}>{x.name}</option>)}
                </select>
              </div>
            )}
            {dlg?.mode === 'edit' && (
              <label className="flex items-center gap-2 text-sm text-[#11182B]"><input type="checkbox" checked={f.active !== false} onChange={(e) => setF('active', e.target.checked)} />Active</label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button data-testid="save-user" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwFor} onOpenChange={(o) => { if (!o) { setPwFor(null); setPwVal(''); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password{pwFor ? ` — ${pwFor.email}` : ''}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>New password</Label><Input data-testid="reset-pw-input" type="text" value={pwVal} onChange={(e) => setPwVal(e.target.value)} placeholder="min 8 characters" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwFor(null); setPwVal(''); }}>Cancel</Button>
            <Button data-testid="confirm-reset-pw" onClick={doReset}>Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export default function Settings() {
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [units, setUnits] = useState([]);
  const [newDept, setNewDept] = useState('');
  const [newDesig, setNewDesig] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [edit, setEdit] = useState(null);
  const [merge, setMerge] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');

  const load = async () => {
    const [d, g, u] = await Promise.all([masterApi.departments(), masterApi.designations(), masterApi.units()]);
    setDepartments(d.data.sort((a, b) => a.name.localeCompare(b.name)));
    setDesignations(g.data.sort((a, b) => (b.level || 0) - (a.level || 0) || a.name.localeCompare(b.name)));
    setUnits(u.data.sort((a, b) => a.name.localeCompare(b.name)));
  };
  useEffect(() => { load(); }, []);

  const wrap = async (fn, ok) => { try { await fn(); toast.success(ok); await load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const openMerge = (kind, name) => { setMerge({ kind, name }); setMergeTarget(''); };
  const doMerge = async () => {
    if (!mergeTarget) { toast.error('Pick a target'); return; }
    const fn = merge.kind === 'department' ? () => masterApi.mergeDepartments(merge.name, mergeTarget)
      : merge.kind === 'unit' ? () => masterApi.mergeUnits(merge.name, mergeTarget)
      : () => masterApi.mergeDesignations(merge.name, mergeTarget);
    await wrap(fn, `Merged "${merge.name}" into "${mergeTarget}"`); setMerge(null);
  };
  const mergeCandidates = merge ? (merge.kind === 'department' ? departments : merge.kind === 'unit' ? units : designations).filter((x) => x.name !== merge.name) : [];

  return (
    <div data-testid="settings-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <h1 className="text-2xl font-semibold tracking-tight text-[#11182B] mb-1">Settings — Masters</h1>
          <p className="text-sm text-[#5A6377] mb-6">Units, departments, designations and labour contractors are shared across all units. Rename or merge to clean duplicates once.</p>
          {/* Designations are twice as wide as the other two masters: a row
              there now carries every role on a level rather than one name, and
              a narrow column would wrap each rung into a block. */}
          {/* Two columns, not three. "Sanber Labs (SGM Nagar)" and "Tool Room
              Maintenance" do not fit a third of the screen, so every name in
              those two masters was being cut off mid-word. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <MasterSection title="Units" items={units} kind="unit" newValue={newUnit} setNewValue={setNewUnit} edit={edit} setEdit={setEdit} wrap={wrap} openMerge={openMerge} />
            <MasterSection title="Departments" items={departmentTree(departments)} kind="department" newValue={newDept} setNewValue={setNewDept} edit={edit} setEdit={setEdit} wrap={wrap} openMerge={openMerge} />
          </div>

          {/* Designations gets the full width of its own: a row there holds
              every role on a level, not one name. */}
          <div className="mt-6">
            <MasterSection title="Designations" items={designations} kind="designation" newValue={newDesig} setNewValue={setNewDesig} edit={edit} setEdit={setEdit} wrap={wrap} openMerge={openMerge} />
          </div>

          <AssociateStructureCard />
          <AssociateDesignationCard />

          <OrgChartLevelsCard />

          <ContractorMasterCard />

          <UnitLetterheadCard />

          <LetterSignatureCard />
          <DocReqCard />

          <UserAdminCard />

          <Dialog open={!!merge} onOpenChange={(o) => { if (!o) setMerge(null); }}>
            <DialogContent>
              <DialogHeader><DialogTitle>Merge {merge?.kind}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-[#5A6377]">Move everyone from <span className="font-semibold text-[#11182B]">{merge?.name}</span> into another {merge?.kind}, then delete <span className="font-semibold text-[#11182B]">{merge?.name}</span>. This can&rsquo;t be undone.</p>
                <div>
                  <Label>Merge into</Label>
                  <select data-testid="merge-target-select" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full" value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)}>
                    <option value="">— select target —</option>
                    {mergeCandidates.map((c) => (<option key={c.id} value={c.name}>{c.name}</option>))}
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMerge(null)}>Cancel</Button>
                <Button data-testid="confirm-merge" onClick={doMerge} disabled={!mergeTarget}>Merge</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
}
